import { useCallback, useMemo } from "react";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import FixaSSRIcon from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/FixaSSRIcon";
import PencilIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/PencilIconPath";
import { ViewerFragmentCommonProps } from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/ViewerFragment";
import EmptyObject from "@coworker/apprestructured/src/task-management/types/EmptyObject";
import styles from "./RefillQuantityViewerFragment.module.css";
import CompletableChecklistItem from "../../../CompletableChecklist/CompletableChecklistItem/CompletableChecklistItem";
import { useTranslation } from "@coworker/locales";
import {
  setByPath,
  getByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import {
  RefillQuantityDetail,
  refillQuantityLabel,
  refillChecklistLabel,
} from "@coworker/apprestructured/src/task-management/services/refillQuantityLabels";
import TitledText from "../../../TitledText/TitledText";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";

export type RefillQuantityViewerFragmentConfigProps = EmptyObject;

type RefillQuantityViewerFragmentProps = {
  configProps: RefillQuantityViewerFragmentConfigProps;
  commonProps: ViewerFragmentCommonProps;
};

function RefillQuantityViewerFragment({
  commonProps,
}: RefillQuantityViewerFragmentProps) {
  const { task, onTaskChange, blockInput } = commonProps;
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParamState();

  const state = getByPath(task, "state");
  const taskId = isLoading(task) ? null : (getByPath(task, "id") as string);

  const refillQuantity = useMemo<RefillQuantityDetail | null>(() => {
    if (isLoading(task)) return null;
    const value = getByPath(task, "details.refill_quantity");
    if (value && typeof value === "object") {
      const refillQuantityValue = value as RefillQuantityDetail;
      return {
        type: refillQuantityValue.type,
        quantity: refillQuantityValue.quantity ?? 0,
        pieces: refillQuantityValue.pieces ?? 0,
      };
    }
    return null;
  }, [task]);

  const isPicked = useMemo<boolean>(() => {
    if (isLoading(task)) return false;
    return getByPath(task, "details.isPicked") === true;
  }, [task]);

  const isRefilled = useMemo<boolean>(() => {
    if (isLoading(task)) return false;
    return getByPath(task, "details.isRefilled") === true;
  }, [task]);

  const handlePickedChange = useCallback(
    (checked: boolean) => {
      if (isLoading(task)) return;
      onTaskChange(setByPath("details.isPicked", checked));
    },
    [task, onTaskChange]
  );

  const handleRefilledChange = useCallback(
    (checked: boolean) => {
      if (isLoading(task)) return;
      onTaskChange(setByPath("details.isRefilled", checked));
    },
    [task, onTaskChange]
  );

  const handleEditClick = useCallback(() => {
    if (taskId) {
      setSearchParams((prev) => {
        prev.delete("taskViewer");
        prev.set("taskEditor", taskId);
      });
    }
  }, [taskId, setSearchParams]);

  const quantityLabel = refillQuantityLabel(refillQuantity, t);

  const pickedLabel = refillChecklistLabel(
    refillQuantity,
    t("pickedLowercaseString"),
    t
  );
  const refilledLabel = refillChecklistLabel(
    refillQuantity,
    t("refilledLowercaseString"),
    t
  );

  const isTaskPending = state === "pending";

  if (!refillQuantity || refillQuantity.quantity === 0) {
    return null;
  }

  return (
    <>
      {isTaskPending && (
        <div>
          <FixaListView
            id="refill-quantity-viewer-fragment"
            className={styles["listView"]}
          >
            <FixaListViewItem
              title={t("refillQuantityString")}
              description={quantityLabel}
              quantityLabel={<FixaSSRIcon paths={PencilIconPath} />}
              emphasised
              inset
              onClick={blockInput ? undefined : handleEditClick}
              control="navigational"
              controlIcon={null}
            />
          </FixaListView>
          <CompletableChecklistItem
            key="picked"
            title={pickedLabel}
            completed={isPicked}
            onCompletedChange={handlePickedChange}
            disabled={blockInput}
          />
          <CompletableChecklistItem
            key="refilled"
            title={refilledLabel}
            completed={isRefilled}
            onCompletedChange={handleRefilledChange}
            disabled={blockInput}
          />
        </div>
      )}
      {!isTaskPending && (
        <TitledText title={t("refillQuantityString")} text={quantityLabel} />
      )}
    </>
  );
}

export default RefillQuantityViewerFragment;
